import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Component from './components/GifNodeView';
import { cleanLinkSrc } from '../../components/Viewer/useHandlers';
import { EXTENSION_TYPE_NAME } from './constants';
import { deleteExtension } from '../../components/utils/tiptap';

export interface GifData {
  url: string;
  width: number;
  height: number;
}

export interface DaGifOptions {
  defaultWidth: number;
  maxWidth?: number;
  minWidth?: number;
  smallMedia?: boolean;
  canAlign?: boolean;
  defaultAlignment?: string;
}

export interface DaGifAttrs {
  alignment: 'left' | 'right' | 'center';
  width: number | null;
  height: number | null;
  url: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    gif: {
      daGifAddGif: (gif: GifData) => ReturnType;
      daGifOpenSettingsMenu: () => ReturnType;
      daGifCloseSettingsMenu: () => ReturnType;
      daGifAlign: (alignment: 'left' | 'right' | 'center') => ReturnType;
      daGifReplace: (gif: GifData) => ReturnType;
      daGifRefresh: (gif: GifData) => ReturnType;
      daGifDelete: () => ReturnType;
    };
  }
}

export enum MenuState {
  HIDDEN,
  SHOW,
}

export type DaGifStorage = {
  menuState: MenuState;
  deviationNode?: any;
};

export const Gif = Node.create<DaGifOptions, DaGifStorage>({
  name: 'da-gif',
  group: 'block unlinkable',
  inline: false,
  draggable: true,

  addStorage() {
    return {
      menuState: MenuState.HIDDEN,
    };
  },

  addOptions() {
    return {
      HTMLAttributes: {},
      defaultWidth: 780,
      smallMedia: false,
      canAlign: true,
    };
  },

  addAttributes() {
    return {
      url: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      alignment: {
        default: this.options.defaultAlignment ?? 'center',
      },
    };
  },

  /**
   * Used to parse incoming html, usually via paste
   */
  parseHTML() {
    return [
      {
        tag: `div[data-da-type="${EXTENSION_TYPE_NAME}"]`,
        getAttrs(node) {
          if (!node || !node.getAttribute) {
            return false;
          }
          const dataType = node.getAttribute('data-da-type');
          if (!dataType || dataType !== EXTENSION_TYPE_NAME) {
            return false;
          }
          try {
            return {
              alignment: node.getAttribute('data-alignment') ?? undefined,
              width: node.getAttribute('data-width') ?? undefined,
              height: node.getAttribute('data-height') ?? undefined,
              url: node.getAttribute('data-url') ?? undefined,
            };
          } catch (e) {}
          return null;
        },
      },
    ];
  },

  renderHTML(props) {
    const {
      HTMLAttributes: { url, width, height, ...rest },
    } = props;

    return [
      'video',
      mergeAttributes(
        {
          autoplay: true,
          loop: true,
          muted: true,
          'data-da-type': EXTENSION_TYPE_NAME,
          'data-width': width,
          'data-height': height,
          width,
          playsInline: true,
          controlsList: 'nofullscreen',
          height,
        },
        rest
      ),
      ['source', { src: cleanLinkSrc(url, ['giphy.com']) }],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component as any);
  },

  onSelectionUpdate() {
    if (this.editor.isActive('da-gif')) {
      this.editor.commands.daGifOpenSettingsMenu();
    } else {
      this.editor.commands.daGifCloseSettingsMenu();
    }
  },

  addCommands() {
    return {
      daGifAddGif:
        (gif: GifData) =>
        ({ commands }) => {
          this.storage.menuState = MenuState.SHOW;
          return commands.insertContent({
            type: this.name,
            attrs: { ...gif },
          });
        },
      daGifOpenSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.SHOW;
          }
          return true;
        },
      daGifCloseSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.HIDDEN;
          }
          return true;
        },
      daGifAlign:
        alignment =>
        ({ editor, chain, dispatch, commands }) => {
          if (!this.options.canAlign) {
            return false;
          }
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              alignment,
            });
          }
          return true;
        },
      daGifDelete:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (deleteExtension(EXTENSION_TYPE_NAME, editor, dispatch, state)) {
            this.storage.menuState = MenuState.HIDDEN;
            return true;
          }
          return true;
        },
      daGifReplace:
        (gif: GifData) =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            commands.updateAttributes(this.name, {
              ...gif,
            });
            return true;
          }
          return true;
        },
    };
  },
});
